import React from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Calendar, Plus, Trash2, RefreshCw, AlertCircle, CloudDownload, CheckCircle, Repeat
} from 'lucide-react';
import { toast } from 'sonner';

export function AdminUnavailabilityTab({ ctx }) {
  const {
    unavailability, setShowUnavailability, handleDeleteUnavailability,
    simplePayStatus, syncingSimplePay, handleSimplePaySync,
    partialLeaveRecords, handleCompletePartialLeave,
  } = ctx;

  return (
    <div className="space-y-6">
      {/* SimplePay Sync Section */}
      <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2"><CloudDownload className="w-5 h-5 text-blue-600" /> SimplePay Leave Sync</h2>
          <Button onClick={handleSimplePaySync} disabled={syncingSimplePay} className="bg-blue-600 hover:bg-blue-700" size="sm" data-testid="sync-simplepay-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncingSimplePay ? 'animate-spin' : ''}`} /> {syncingSimplePay ? 'Syncing...' : 'Sync Now'}
          </Button>
        </div>
        {simplePayStatus && (
          <div className="text-xs text-stone-500">
            Last sync: {simplePayStatus.last_sync ? new Date(simplePayStatus.last_sync).toLocaleString() : 'Never'}
          </div>
        )}
      </div>

      {/* Partial Leave Records */}
      {partialLeaveRecords?.length > 0 && (
        <div className="bg-white rounded-xl shadow-md border border-amber-200 p-6">
          <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2 mb-4"><AlertCircle className="w-5 h-5 text-amber-600" /> Partial Leave - Set Times ({partialLeaveRecords.length})</h2>
          <div className="space-y-3">
            {partialLeaveRecords.map(record => (
              <div key={record.id} className="border border-amber-200 bg-amber-50 rounded-lg p-3">
                <div className="flex items-center justify-between">
                  <div><span className="font-semibold">{record.username}</span> <span className="text-sm text-stone-600">- {record.leave_type?.replace('_', ' ')}</span></div>
                  <div className="text-sm text-stone-600">{record.leave_date} ({record.hours}h)</div>
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Input type="time" id={`partial-start-${record.id}`} className="w-32" placeholder="Start" />
                  <span className="text-stone-500">to</span>
                  <Input type="time" id={`partial-end-${record.id}`} className="w-32" placeholder="End" />
                  <Button onClick={() => {
                    const start = document.getElementById(`partial-start-${record.id}`)?.value;
                    const end = document.getElementById(`partial-end-${record.id}`)?.value;
                    if (start && end) handleCompletePartialLeave(record.id, start, end);
                    else toast.error('Set both start and end times');
                  }} size="sm" className="bg-amber-600 hover:bg-amber-700"><CheckCircle className="w-4 h-4 mr-1" /> Set</Button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Unavailability Management */}
      <div className="bg-white rounded-xl shadow-md border border-stone-200 p-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-stone-800 flex items-center gap-2"><Calendar className="w-5 h-5 text-blue-600" /> Unavailability Management</h2>
          <Button onClick={() => setShowUnavailability(true)} data-testid="add-unavailability-btn"><Plus className="w-4 h-4 mr-2" /> Add Unavailability</Button>
        </div>
        {unavailability?.length > 0 ? (
          <div className="space-y-3">
            {unavailability.map(unavail => (
              <div key={unavail.id} className="border border-stone-200 bg-stone-50 rounded-lg p-3 flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm text-stone-800">{unavail.username}</span>
                    {unavail.source === 'recurring' && (
                      <span className="px-1.5 py-0.5 bg-violet-100 text-violet-700 text-[10px] font-bold rounded flex items-center gap-0.5">
                        <Repeat className="w-2.5 h-2.5" /> Recurring
                      </span>
                    )}
                    {unavail.simplepay_leave_id && (
                      <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 text-[10px] font-bold rounded">SimplePay</span>
                    )}
                  </div>
                  <div className="text-sm text-stone-600 mt-1">
                    {new Date(unavail.date_from).toLocaleDateString()} - {new Date(unavail.date_to).toLocaleDateString()}
                    {unavail.hours ? (
                      <span className="ml-2 px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                        {unavail.hours}h partial
                      </span>
                    ) : (
                      <span className="ml-2 px-1.5 py-0.5 bg-stone-100 text-stone-600 rounded text-xs">Full day</span>
                    )}
                  </div>
                  <div className="text-xs text-stone-500 mt-0.5">{unavail.reason}</div>
                </div>
                {!unavail.simplepay_leave_id && (
                  <Button variant="ghost" size="sm" onClick={() => handleDeleteUnavailability(unavail.id)} className="text-red-600 hover:text-red-700 hover:bg-red-50" data-testid={`delete-unavail-${unavail.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-12 text-stone-500">
            <Calendar className="w-12 h-12 mx-auto mb-3 text-stone-400" />
            <p>No unavailability periods set</p>
          </div>
        )}
      </div>
    </div>
  );
}
